import {GroupFilterContext} from 'context/groupFilterContext';
import {useGetTranslatedCategory} from 'hooks/useGetTranslatedCategory';
import {useTranslation} from 'hooks/translations/useCustomTranslation';
import React, {useContext} from 'react';
import {useFormContext} from 'react-hook-form';

import {palette} from 'styles/mui/scss';

import {Box, Progress, Typography} from 'components/basic-components';

import {CarFilterCategorySwitch} from './CarFilterCategorySwitch';
import CarFilterCheckbox from './CarFilterCheckbox';
import CarFilterRadio from './CarFilterRadio';
import CarFilterSelect from './CarFilterSelect';
import CarFilterSlider from './CarFilterSlider';
import getFilterSettings from './getFilterSettings';
import styles from './styles/CarFilter.module.scss';

const SeperatorLine = ({sx = {}}) => {
  return <Box className={styles.seperator} sx={sx} />;
};
const CarFilter = ({setDisplayFilter}: {setDisplayFilter?: any}) => {
  const {t} = useTranslation('carFilter');
  const {reset} = useFormContext();
  const {
    filteredListWithoutAge,
    filteredList,
    groupsData,
    currentCategory,
    minPrice,
    maxPrice,
    withPrice,
  } = useContext(GroupFilterContext);
  const {type, extras, age, licence, features} = getFilterSettings(t);

  const groupTypes = [
    ...new Set(
      groupsData.filter(group => group.category === currentCategory).map(group => group.type),
    ),
  ];
  const getTranslatedCategory = useGetTranslatedCategory();
  return (
    <form>
      <Box className={styles.filterWrapper}>
        <Box className={styles.filterColumn}>
          <Box className={styles.checkboxFilterWrapper}>
            <CarFilterCategorySwitch />
            {currentCategory === getTranslatedCategory('pkw') ? (
              <>
                <CarFilterCheckbox
                  label={features.pkw.premium.label}
                  name={features.pkw.premium.name}
                  data={filteredList}
                  iconName={features.pkw.premium.iconName}
                />
                <CarFilterCheckbox
                  label={features.pkw.electric.label}
                  name={features.pkw.electric.name}
                  data={filteredList}
                  iconName={features.pkw.electric.iconName}
                />
                <CarFilterCheckbox
                  label={features.pkw.payment.label}
                  name={features.pkw.payment.name}
                  data={filteredList}
                  iconName={features.pkw.payment.iconName}
                />
              </>
            ) : currentCategory === getTranslatedCategory('lkw') ? (
              <CarFilterCheckbox
                label={features.lkw.lifting.label}
                name={features.lkw.lifting.name}
                data={filteredList}
                iconName={features.lkw.lifting.iconName}
              />
            ) : null}
          </Box>
          <CarFilterSelect
            inputName={type.name}
            filter={groupTypes}
            title={type.label}
            data={filteredList}
          />
          <SeperatorLine sx={{display: {xxs: 'block', sm: 'none', xl: 'block'}}} />
        </Box>
        <Box className={styles.filterColumn}>
          <Box
            sx={{
              '& div .MuiAccordion-root .MuiAccordionSummary-root': {
                minHeight: {sm: 'unset', xl: '48px'},
                alignItems: {sm: 'flex-start', xl: 'center'},
                '& .MuiAccordionSummary-content': {padding: {sm: '0 0 15px 0', xl: '15px 0'}},
              },
            }}
          >
            <CarFilterSelect
              inputName={extras.name}
              filter={currentCategory === getTranslatedCategory('lkw') ? extras.lkw : extras.pkw}
              title={extras.label}
              data={filteredList}
            />
          </Box>
          <SeperatorLine />
          {currentCategory !== getTranslatedCategory('lkw') ? (
            <CarFilterRadio
              inputName={age.name}
              filter={age.pkw}
              title={age.label}
              data={filteredListWithoutAge}
            />
          ) : (
            <CarFilterSelect
              inputName={licence.name}
              filter={licence.lkw}
              title={licence.label}
              data={filteredList}
            />
          )}
          {withPrice ? <SeperatorLine sx={{display: {xxs: 'block', sm: 'none', xl: 'block'}}} /> : null}
        </Box>
        {withPrice ? (
          <>
            <SeperatorLine sx={{display: {xxs: 'none', sm: 'block', xl: 'none'}, width: '49%'}} />
            <SeperatorLine sx={{display: {xxs: 'none', sm: 'block', xl: 'none'}, width: '49%'}} />
          </>
        ) : null}
        {withPrice ? (
          <Box sx={{width: '100%'}}>
            {minPrice && maxPrice ? (
              <CarFilterSlider
                title={t('price')}
                inputName={'price'}
                key={`${maxPrice}-${minPrice}`}
                minPrice={minPrice}
                maxPrice={maxPrice}
              />
            ) : (
              <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Progress strokeColor={palette.colorBlack} width="40" />
              </Box>
            )}
          </Box>
        ) : null}
        <Box className={styles.filterActionsWrapper}>
          <SeperatorLine />
          <button
            onClick={e => {
              e.preventDefault();
              reset();
            }}
            className={styles.resetButton}
          >
            <Typography className={styles.resetButtonText}>{t('resetFilter')}</Typography>
          </button>
          <button
            className={styles.applyFilterButton}
            onClick={e => {
              e.preventDefault();
              setDisplayFilter(false);
            }}
          >
            <Typography className={styles.applyFilterButtonText}>{t('applyFilter')}</Typography>
          </button>
        </Box>
      </Box>
    </form>
  );
};

export default CarFilter;
